/**
 * Canonical lineage types.
 *
 * Lineage records structural transformation over time.
 * It does not represent ordinary closure outcomes such as "resolved as reframe".
 */

export type LineageEventType =
  | 'split'
  | 'merge'
  | 'supersede'
  | 'retire'
  | 'fork';

export interface LineageEvent {
  id: string;
  eventType: LineageEventType;
  sourceItemIds: string[];
  targetItemIds: string[];
  rationale?: string;
  performedBy?: string;
  createdAt: Date;
  metadata?: Record<string, unknown>;
  workspaceId?: string;
}

export interface LineageEventRow {
  id: string;
  event_type: LineageEventType;
  source_item_ids: string[];
  target_item_ids: string[];
  rationale?: string | null;
  performed_by?: string | null;
  created_at: string;
  metadata?: Record<string, unknown> | null;
  workspace_id?: string | null;
}

export interface LineageRelationshipResolution {
  id: string;
  lineageEventId: string;
  relationshipId: string;
  relationshipSource: 'group' | 'edge';
  resolutionAction: string;
  resolutionNote?: string;
  resolvedBy?: string;
  resolvedAt: Date;
  workspaceId?: string;
}

export interface LineageRelationshipResolutionRow {
  id: string;
  lineage_event_id: string;
  relationship_id: string;
  relationship_source: 'group' | 'edge';
  resolution_action: string;
  resolution_note?: string | null;
  resolved_by?: string | null;
  resolved_at: string;
  workspace_id?: string | null;
}

export function lineageEventRowToDomain(row: LineageEventRow): LineageEvent {
  return {
    id: row.id,
    eventType: row.event_type,
    sourceItemIds: row.source_item_ids,
    targetItemIds: row.target_item_ids,
    rationale: row.rationale ?? undefined,
    performedBy: row.performed_by ?? undefined,
    createdAt: new Date(row.created_at),
    metadata: row.metadata ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
  };
}

export function lineageRelationshipResolutionRowToDomain(
  row: LineageRelationshipResolutionRow,
): LineageRelationshipResolution {
  return {
    id: row.id,
    lineageEventId: row.lineage_event_id,
    relationshipId: row.relationship_id,
    relationshipSource: row.relationship_source,
    resolutionAction: row.resolution_action,
    resolutionNote: row.resolution_note ?? undefined,
    resolvedBy: row.resolved_by ?? undefined,
    resolvedAt: new Date(row.resolved_at),
    workspaceId: row.workspace_id ?? undefined,
  };
}
